import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"

import { getTurnstilePublicKey } from "@/cloudflare/turnstile"
import config from "@/config"
import { ContactForm } from "@/contact/components/contact-form"
import { localizeHref } from "@/core/i18n/locale-context"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"

import { Breadcrumb } from "../../components/landing"

/** Chinese version of /contact (D-WEB-LANG-SCOPE). The form labels and messages are localised in contact-text.ts. */
export const contactZhSeo = () =>
  buildHead({
    title: "联系我们",
    description:
      "就你的行程、收据、发票或账户数据，向 Kestly 提问、报告故障，或就你的行程、收据、发票和账户数据提出请求。每条消息我们都会看，并通过邮件回复。",
    path: "/contact",
    localePrefix: "zh",
    jsonLd: [breadcrumbJsonLd([{ name: "首页", path: "/zh" }, { name: "联系我们" }])],
  })

export const Route = createFileRoute("/_brand-layout/zh/contact")({
  head: () => {
    const { meta, links, scripts } = contactZhSeo()
    return { meta, links, scripts }
  },
  component: ContactZhRoute,
})

/** The page is prerendered, so the per-environment Turnstile key is fetched in the browser. */
function ContactZhRoute() {
  const { data } = useQuery({ queryKey: ["contact-page"], queryFn: () => getTurnstilePublicKey() })
  return <ContactZhPage cloudflareTurnstilePublicKey={data?.cloudflareTurnstilePublicKey} />
}

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

export function ContactZhPage({
  cloudflareTurnstilePublicKey,
}: {
  cloudflareTurnstilePublicKey?: string
}) {
  return (
    <>
      <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "联系我们" }]} />
      <header className="bg-muted/40 px-band py-8 md:py-12">
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">联系我们</h1>
        <p className="text-muted-foreground mt-3 max-w-[60ch] text-lg">
          有关于你的记录的问题、发现故障，或想提出与你的数据有关的请求？写信给我们，我们会通过邮件回复。你在这里填写的内容只用来回复你，见
          <a href={localizeHref("/legal/privacy", "zh")} className={linkClass}>
            隐私政策（英文）
          </a>
          。
        </p>
      </header>
      <section className="px-band py-8">
        <ContactForm
          cloudflareTurnstilePublicKey={cloudflareTurnstilePublicKey}
          className="max-w-xl"
        />
        <p className="text-muted-foreground mt-6 max-w-xl">
          更喜欢发邮件？请写信到{" "}
          <a
            href={`mailto:${config.core.supportEmail}`}
            className="focus-visible:ring-ring inline-flex min-h-11 items-center rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
          >
            {config.core.supportEmail}
          </a>
          。由真人回复，我们不保证回复时间。常见问题的解答见
          <a href={`${localizeHref("/faq", "zh")}#app`} className={linkClass}>
            常见问题
          </a>
          。
        </p>
      </section>
    </>
  )
}
